/**
 * Admin Dashboard Query Hook
 *
 * Fetches dashboard data with polling for real-time updates.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getDashboardData } from '../../api/dashboard';
import { queryKeys, POLL_INTERVALS } from '../../lib/queryClient';
import { getErrorMessage } from '../../api/client';
import { useOrganization } from '../../context/OrganizationContext';

interface UseAdminDashboardOptions {
  /** Enable/disable automatic polling (default: true) */
  polling?: boolean;
  /** Custom polling interval in ms (default: 30000) */
  pollingInterval?: number;
}

export function useAdminDashboard(options: UseAdminDashboardOptions = {}) {
  const { polling = true, pollingInterval = POLL_INTERVALS.DASHBOARD } = options;
  const { currentOrg, isLoading: orgLoading } = useOrganization();

  const query = useQuery({
    queryKey: [...queryKeys.adminDashboard, currentOrg?.orgSlug],
    queryFn: getDashboardData,
    // Only enable query when org context is ready
    enabled: !orgLoading && !!currentOrg,
    // Enable polling when specified
    refetchInterval: polling ? pollingInterval : false,
    // Stop polling when window is not visible
    refetchIntervalInBackground: false,
    // Consider data stale after half the poll interval
    staleTime: pollingInterval / 2,
  });

  return {
    // Data - destructured for convenience
    data: query.data ?? null,
    stats: query.data?.stats ?? null,
    upcomingEvents: query.data?.upcomingEvents ?? [],
    pendingAssignments: query.data?.pendingAssignments ?? [],

    // States - include org loading in overall loading state
    loading: orgLoading || query.isLoading,
    error: query.error ? getErrorMessage(query.error) : null,
    isRefetching: query.isRefetching,
    isStale: query.isStale,

    // Timestamps for UI
    dataUpdatedAt: query.dataUpdatedAt,

    // Actions
    refetch: query.refetch,
  };
}

/**
 * Hook for invalidating admin dashboard data after mutations
 */
export function useInvalidateAdminDashboard() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.adminDashboard });
  };
}
